import { useMemo, type ReactNode } from "react";
import { alpha, createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { useMaterialUIController } from "context";

// Scope the board's visual language to this feature, including dialogs rendered in portals.
// The dashboard template otherwise paints contained error buttons white on white.
export default function FutureBoardTheme({ children }: { children: ReactNode }) {
  const outer = useTheme();
  const [controller] = useMaterialUIController();
  const dark = controller.darkMode;
  const theme = useMemo(() => {
    const ink = dark ? "#edf2fa" : "#344767";
    const muted = dark ? "#c0cada" : "#56647b";
    const info = outer.palette.info;
    // The dashboard's overdue-task tile uses the primary gradient (pink/red).
    const danger = outer.palette.gradients.primary;
    const dangerGradient = outer.functions.linearGradient(danger.main, danger.state);
    const infoFocus = (info as typeof info & { focus?: string }).focus ?? info.dark;
    const accent = dark ? info.light : info.main;
    const paper = dark ? "#202940" : "#ffffff";
    const line = dark ? "#46536b" : "#dce2eb";
    const fontFamily = '"Inter", "Noto Sans JP", "Yu Gothic", "Meiryo", sans-serif';
    return createTheme(outer, {
      typography: {
        fontFamily,
        ...Object.fromEntries(
          [
            "h1",
            "h2",
            "h3",
            "h4",
            "h5",
            "h6",
            "subtitle1",
            "subtitle2",
            "body1",
            "body2",
            "caption",
            "button",
            "overline",
          ].map((variant) => [
            variant,
            {
              fontFamily,
              ...(["body1", "body2", "caption"].includes(variant) ? { fontWeight: 400 } : {}),
            },
          ])
        ),
      },
      palette: {
        mode: dark ? "dark" : "light",
        text: { primary: ink, secondary: muted, disabled: dark ? "#a4b4bc" : "#61717d" },
        background: { paper },
        divider: line,
      },
      components: {
        // The dashboard Select override removes vertical padding and hides the
        // arrow. Restore the outlined control's height within the future board.
        MuiSelect: {
          styleOverrides: {
            select: {
              display: "block", minHeight: "1.5em", lineHeight: 1.5,
              padding: "16px 40px 16px 14px !important",
              "&.MuiInputBase-inputSizeSmall": { padding: "11px 40px 11px 14px !important" },
            },
            icon: { display: "block", right: 10, color: muted },
          },
        },
        MuiSvgIcon: { defaultProps: { fontSize: "medium" } },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              minHeight: 44,
              fontWeight: 700,
              textTransform: "none",
              lineHeight: 1.5,
              "&&.MuiButton-text:not(.MuiButton-colorInherit)": { color: accent },
              "&&.MuiButton-text.MuiButton-textError": { color: dark ? outer.palette.primary.light : danger.state },
              "&&.MuiButton-outlined": {
                color: dark ? info.light : infoFocus,
                borderColor: info.main,
                backgroundColor: alpha(info.main, dark ? 0.18 : 0.08),
                "&:hover, &:focus-visible": {
                  color: dark ? info.light : infoFocus,
                  borderColor: accent,
                  backgroundColor: alpha(info.main, dark ? 0.28 : 0.14),
                },
              },
              "&&.MuiButton-outlined.Mui-disabled": {
                backgroundColor: dark ? "#344159" : "#e1e7eb",
              },
              "&&.MuiButton-contained": {
                color: "#ffffff",
                backgroundColor: info.main,
                boxShadow: `0 4px 12px ${alpha(info.main, 0.18)}`,
                "&:hover, &:focus:not(:hover)": { color: "#ffffff", backgroundColor: infoFocus },
              },
              "&&.MuiButton-containedError": {
                color: "#ffffff",
                backgroundColor: outer.palette.primary.main,
                backgroundImage: dangerGradient,
                boxShadow: `0 4px 12px ${alpha(danger.state, 0.24)}`,
                "&:hover, &:focus:not(:hover)": { color: "#ffffff", backgroundColor: danger.state, backgroundImage: dangerGradient },
              },
              "&&.MuiButton-root.Mui-disabled": {
                color: dark ? "#a4b4bc" : "#61717d",
                borderColor: line,
                boxShadow: "none",
              },
              "&&.MuiButton-contained.Mui-disabled": {
                color: dark ? "#c4d1d6" : "#52616e",
                backgroundColor: dark ? "#344159" : "#e1e7eb",
                backgroundImage: "none",
              },
              "&&.Mui-focusVisible": { outline: `3px solid ${accent}`, outlineOffset: 3 },
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              color: muted,
              minWidth: 40,
              minHeight: 40,
              "&.Mui-disabled": { color: dark ? "#91a5af" : "#87969e" },
              "&.Mui-focusVisible": { outline: `3px solid ${accent}`, outlineOffset: 2 },
            },
          },
        },
        MuiTab: { styleOverrides: { root: { color: `${muted} !important` } } },
        MuiCardActionArea: {
          styleOverrides: {
            root: {
              "&.Mui-focusVisible": { outline: `3px solid ${accent}`, outlineOffset: -3 },
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: { borderRadius: 24, backgroundColor: paper, backgroundImage: "none" },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: { padding: "28px 28px 16px", color: ink, fontSize: "1.25rem", lineHeight: 1.6 },
          },
        },
        MuiDialogContent: { styleOverrides: { root: { padding: "12px 28px 24px" } } },
        MuiDialogContentText: {
          styleOverrides: { root: { color: muted, fontSize: "0.9375rem", lineHeight: 1.9 } },
        },
        MuiDialogActions: {
          styleOverrides: { root: { padding: "16px 24px 24px", gap: 8, flexWrap: "wrap" } },
        },
      },
    });
  }, [outer, dark]);
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
